<script setup lang="ts">
/**
 * 运营端外壳 —— 「有没有令牌」决定套哪一层模板。
 *
 * 登录与未登录长得完全不一样:前者是深色侧栏 + 深色内容区(v2 的壳),后者是
 * 品牌区 + 一张登录卡。这个判断从 S-10 起就在这里,改版只是把模板换成了
 * AppShell / LoginView(登录页自己内含 LoginLayout —— O8 的整页文案都住在
 * views/LoginView.vue 里,design-copy 的那一帧因此只需要指向一个文件)。
 *
 * ═══ 深色(2026-10-16,S-41 / AS-069 / Y-31) ═══
 *
 * tone="ops-dark":内容区取深色家族、侧栏取画板那条渐变。数据与令牌是同一个来源
 * (packages/ui),这里只是声明「这一端归哪一档」——它同时是守卫⑥的判据。
 *
 * ═══ 导航只列已经能点通的链路 ═══
 *
 * 空菜单项会让人以为功能有了,点进去才发现是空的 —— 这条纪律从 S-01 起
 * 就写在这个文件里,改版时保留。结算与对账(S-43)建成后进来了;审计日志
 * 由 S-44 建成后进来了(Y-32 的七项至此齐了);画板里那个「数据大屏」入口
 * 没有对应页面,不渲染(这是 Y-32 记录的裁决,不是遗漏)。NAV ↔ 路由 ↔
 * 视图文件三个方向由 guards/check-admin-nav.mjs 双向核对。
 *
 * 顺序按 Y-32 的清单:订单驾驶舱 → 结算与对账 → 审计日志 → 计价表
 * → 承运商入驻 → 平台配置 → 监管端账号。画板侧栏那一列画的词(订单 /
 * 结算 / 计价 / 承运商 / 配置)与这里的词不完全一样 —— 以 Y-32 的清单为准,
 * 画板那几个词登记在 design-copy 的豁免里。
 *
 * 配置提供者(message / config)由 AppShell 与 LoginLayout 各自内含 ——
 * 视图里的 useMessage() 照常用,不要在这一层再套一层(套两层之后
 * useMessage() 拿到的是外层那个,而外层没有挂载点时它会抛一句和视图无关的错)。
 */
import { computed, ref } from 'vue'
import { RouterView, useRoute, useRouter } from 'vue-router'
import { AppShell, type NavItem } from '@sea-drone/ui'
import { clearToken, hasToken } from './api/client'

const router = useRouter()
const route = useRoute()
const loggedIn = ref(hasToken())

// 路由一变就重新看一眼令牌 —— 登录/登出之后导航栏要跟着变
router.afterEach(() => {
  loggedIn.value = hasToken()
})

const showShell = computed(() => loggedIn.value && route.path !== '/login')

/** 七个入口(Y-32 的清单至此齐了),图标名取自设计规格的 IconName */
const NAV: readonly NavItem[] = [
  { key: 'orders', label: '订单驾驶舱', to: '/orders', icon: 'orders' },
  // S-43:结算与对账(画板 v2 · O2)。图标是那一板上画的那只钱包
  { key: 'settlements', label: '结算与对账', to: '/settlements', icon: 'wallet' },
  // S-44:审计日志(画板 v2 · O3)。画板那一格画的是 file-text / scroll-text,
  // 本仓不引图标库 —— 自画一只「几行流水 + 一枚放大镜」,与相邻的订单图标区分开
  { key: 'audit-logs', label: '审计日志', to: '/audit-logs', icon: 'audit' },
  // 这一页原来叫「计价表与试算」—— 试算面板 2026-09-22 按 N-07 撤掉了,导航跟着改
  { key: 'pricing-tables', label: '计价表', to: '/pricing-tables', icon: 'pricing' },
  { key: 'carriers', label: '承运商入驻', to: '/carriers', icon: 'carriers' },
  { key: 'config', label: '平台配置', to: '/config', icon: 'config' },
  { key: 'gov-accounts', label: '监管端账号', to: '/gov-accounts', icon: 'accounts' },
]

/**
 * 侧栏底部的海报位(画板 O1:180×110 图 + 两行字)。
 * 图是仓库根的 home-hero.png 拷进 public/ 的副本 —— 相对路径引用,
 * 打 dev 与打 build 都从站点根取(见 apps/admin-ops/public/)。
 */
const POSTER = {
  src: '/home-hero.png',
  title: '低空物流 · 智联海岛',
  subtitle: '安全高效 · 全程可溯',
}

function logout(): void {
  clearToken()
  loggedIn.value = false
  void router.push('/login')
}
</script>

<template>
  <AppShell
    v-if="showShell"
    tone="ops-dark"
    product="运营后台"
    :nav="NAV"
    :poster="POSTER"
    @logout="logout"
  >
    <router-view />
  </AppShell>
  <!-- 登录页自带 LoginLayout(它同时承载 O8 的整页文案):这里只放路由出口 -->
  <router-view v-else />
</template>
